import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQalert } from '.';

export interface PopupWindowConfig {
  title: string | undefined;
  toolbar?: boolean;
  menubar?: boolean;
  location?: boolean;
  width?: number;
  height?: number;
  top?: number;
  left?: number;
}

const defaultPopupWindowConfig: Required<PopupWindowConfig> = {
  title: undefined,
  toolbar: false,
  menubar: false,
  location: false,
  width: 600,
  height: 700,
  top: 100,
  left: 100,
};

export interface PopupWindowOnCloseCallback {
  (): void | Promise<void>;
}
export interface PopupWindowHookResult {
  openWindow: (url: string, config?: PopupWindowConfig) => void;
  isWindowOpen: boolean;
}

/**
 * Creates a new "uncontrolled" pop-up window that opens the given URL.
 *
 * Using this hook ensures only 1 pop-up is open at any given time.
 *
 * @param url
 * @param config
 */
export const usePopupWindow = (): PopupWindowHookResult => {
  const [, toggleSnackbar] = useQalert();
  const [windowUrl, setWindowUrl] = useState<string | undefined>(undefined);
  const [popupWindow, setPopupWindow] = useState<Window | null | undefined>(undefined);

  const isWindowClosed = (window: Window | null | undefined) => !window || window.closed;

  const openWindow = useCallback(
    (url: string, config?: PopupWindowConfig): void => {
      const { title, toolbar, menubar, location, width, height, top, left } = {
        ...defaultPopupWindowConfig,
        ...config,
      };

      // feature string per doc - https://developer.mozilla.org/en-US/docs/Web/API/Window/open#syntax
      const windowFeatures =
        `toolbar=${toolbar ? 'yes' : 'no'}, menubar=${menubar ? 'yes' : 'no'}, ` +
        `location=${location ? 'yes' : 'no'}` +
        `width=${width}, height=${height}, top=${top}, left=${left}`;

      const isUrlDifferent = url !== windowUrl;
      // Boolean() here catches `null` and `undefined`

      // since useState setters are asynchronous, we should keep track of the state for this method call
      let windowToUse = popupWindow;
      let urlToUse = windowUrl;

      // url is different, update the URL have in the window
      if (isUrlDifferent) {
        urlToUse = url;
        setWindowUrl(url);

        if (!isWindowClosed(windowToUse)) {
          windowToUse?.location.assign(url);
        }
      }

      // if window is not open, open it
      if (urlToUse && isWindowClosed(windowToUse)) {
        windowToUse = window.open(urlToUse, title, windowFeatures);
        setPopupWindow(windowToUse);

        // check if the pop-up was blocked
        if (isWindowClosed(windowToUse)) {
          toggleSnackbar({
            open: true,
            text: 'Error opening pop-up. Please check if the pop-up was blocked.',
            type: 'error',
          });
          return;
        }
      }

      // things that should always happen, if the window exists
      if (isWindowClosed(windowToUse)) {
        windowToUse?.focus();
      }
    },
    [popupWindow, toggleSnackbar, windowUrl]
  );

  useEffect(() => {
    if (popupWindow) {
      const interval = setInterval(() => {
        if (isWindowClosed(popupWindow)) {
          // this will force a re-render and update necessary state
          setPopupWindow(undefined);
        }
      }, 500); // every 0.5 seconds

      return () => clearInterval(interval);
    }
  }, [popupWindow]);

  return { openWindow, isWindowOpen: !isWindowClosed(popupWindow) };
};
